import React, { useEffect, useState } from 'react';
import { Calendar, Users, Image, Clock } from 'lucide-react';
import { getEvents, getServices, getGalleryItems } from '../../lib/api';
import toast from 'react-hot-toast';
import type { Event, Service, GalleryItem } from '../../lib/types';

export default function Dashboard() {
  const [stats, setStats] = useState({
    events: 0,
    services: 0,
    gallery: 0,
    lastUpdate: ''
  });
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState<Event[]>([]);

  useEffect(() => {
    async function fetchStats() {
      try {
        const [eventsData, servicesData, galleryData] = await Promise.all([
          getEvents(),
          getServices(),
          getGalleryItems()
        ]);

        setStats({
          events: eventsData.length,
          services: servicesData.length,
          gallery: galleryData.length,
          lastUpdate: new Date().toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })
        });

        setRecentActivities(eventsData.slice(0, 5));
      } catch (error) {
        console.error('Error fetching stats:', error);
        toast.error('Errore nel caricamento delle statistiche');
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, []);

  const statsConfig = [
    { name: 'Eventi Totali', value: stats.events, icon: Calendar },
    { name: 'Servizi Attivi', value: stats.services, icon: Users },
    { name: 'Immagini Galleria', value: stats.gallery, icon: Image },
    { name: 'Ultimo Aggiornamento', value: stats.lastUpdate, icon: Clock },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statsConfig.map((stat) => (
          <div
            key={stat.name}
            className="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden"
          >
            <dt>
              <div className="absolute bg-pink-500 rounded-md p-3">
                <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                {stat.name}
              </p>
            </dt>
            <dd className="ml-16 pb-6 flex items-baseline sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
            </dd>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900">Attività Recenti</h3>
          <div className="mt-6 flow-root">
            <ul className="-mb-8 divide-y divide-gray-200">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => (
                  <li key={activity.id} className="py-4">
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-pink-500 flex items-center justify-center ring-8 ring-white">
                          <Calendar className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">
                            {activity.status === 'published' ? 'Evento pubblicato:' : 'Bozza evento:'}{' '}
                            <span className="font-medium text-gray-900">
                              {activity.title}
                            </span>
                          </p>
                          <p className="mt-1 text-xs text-gray-500">{activity.location}</p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          <time dateTime={activity.created_at}>
                            {new Date(activity.created_at).toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </time>
                        </div>
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                <li className="py-4 text-center text-gray-500">
                  Nessuna attività recente
                </li>
              )}
            </ul>
          </div>
          <div className="mt-6 text-center">
            <a
              href="/admin/events"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-pink-600 bg-pink-50 hover:bg-pink-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
            >
              Visualizza tutti gli eventi
              <Calendar className="ml-2 -mr-1 h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}